import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/design-system/layout/AppLayout";
import { Sparkles, Copy, Edit3 } from "lucide-react";

export default function CreateEvent() {
  const navigate = useNavigate();
  const [selectedPath, setSelectedPath] = useState<"ai" | "clone" | "scratch" | null>(null);
  const [aiDescription, setAiDescription] = useState("");

  const handleAISubmit = () => {
    if (aiDescription.trim()) {
      // Would parse the description and create event
      console.log("Creating event from AI description:", aiDescription);
      navigate("/events/new/edit");
    }
  };

  const handleClone = () => {
    // Would show clone dialog or navigate to clone selection
    navigate("/events/new/edit");
  };

  const handleBuildFromScratch = () => {
    navigate("/events/new/edit");
  };

  const pastEvents = [
    { id: 1, name: "KC Youth Basketball Classic", date: "May 15-16, 2024" },
    { id: 2, name: "Girls Volleyball Showcase", date: "May 20-21, 2024" },
    { id: 3, name: "Spring Classic 2024", date: "April 1-3, 2024" },
  ];

  return (
    <AppLayout userName="Sarah Johnson" userRole="Tournament Operator">
      <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Create Event</h1>
            <p className="text-muted-foreground mt-2">
              Choose how you'd like to create your new tournament event
            </p>
          </div>

          {/* 3-way fork cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* 1. AI-Assisted (Recommended) */}
            <div
              className={`relative rounded-2xl border-2 transition-all ${
                selectedPath === "ai"
                  ? "border-primary bg-primary/5 shadow-lg"
                  : "border-border bg-white hover:border-primary/50"
              }`}
            >
              {/* Recommended badge */}
              {selectedPath !== "ai" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                    RECOMMENDED
                  </span>
                </div>
              )}

              <div className="p-6 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-bold text-foreground">
                        Describe in Plain English
                      </h3>
                    </div>
                    <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">
                      ✨ AI-assisted
                    </span>
                  </div>
                </div>

                {/* Content */}
                {selectedPath === "ai" ? (
                  <div className="flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground mb-4">
                      Tell us about your event in plain language. Our AI will help you set it up.
                    </p>

                    <textarea
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      placeholder="Example: 16-team boys basketball showcase at Lifetime Athletic in Overland Park, KS. May 15-16. $695 entry fee. Divisions for 14U, 15U, 16U, 17U."
                      className="flex-1 w-full rounded-lg border border-border bg-background p-3 text-sm text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                      rows={6}
                    />

                    <button
                      onClick={handleAISubmit}
                      disabled={!aiDescription.trim()}
                      className="mt-4 w-full bg-primary text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground">
                      Write a quick description of your tournament and our AI will extract the key details to get you started quickly.
                    </p>

                    <button
                      onClick={() => setSelectedPath("ai")}
                      className="mt-4 w-full bg-primary text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Try AI-Assisted
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 2. Clone from Previous */}
            <div
              className={`rounded-2xl border-2 transition-all ${
                selectedPath === "clone"
                  ? "border-primary bg-primary/5 shadow-lg"
                  : "border-border bg-white hover:border-primary/50"
              }`}
            >
              <div className="p-6 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start gap-2 mb-4">
                  <Copy className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground">
                      Clone from Previous
                    </h3>
                  </div>
                </div>

                {/* Content */}
                {selectedPath === "clone" ? (
                  <div className="flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground mb-4">
                      Select an event to duplicate
                    </p>

                    <div className="space-y-2 mb-4 flex-1">
                      {pastEvents.map((event) => (
                        <div
                          key={event.id}
                          className="border border-border rounded-lg p-3 hover:bg-secondary transition-colors cursor-pointer"
                        >
                          <p className="font-medium text-foreground text-sm">
                            {event.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {event.date}
                          </p>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleClone}
                      className="w-full bg-primary text-white font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Clone Selected Event
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground">
                      Quickly create a new event by cloning settings from a previous tournament.
                    </p>

                    <button
                      onClick={() => setSelectedPath("clone")}
                      className="mt-4 w-full bg-secondary text-foreground font-semibold py-2.5 rounded-lg hover:bg-muted transition-colors"
                    >
                      Clone Event
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 3. Build from Scratch */}
            <div
              className={`rounded-2xl border-2 transition-all ${
                selectedPath === "scratch"
                  ? "border-primary bg-primary/5 shadow-lg"
                  : "border-border bg-white hover:border-primary/50"
              }`}
            >
              <div className="p-6 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start gap-2 mb-4">
                  <Edit3 className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground">
                      Build from Scratch
                    </h3>
                  </div>
                </div>

                {/* Content */}
                {selectedPath === "scratch" ? (
                  <div className="flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground">
                      Open the full event editor with all fields empty. You'll configure everything manually.
                    </p>

                    <button
                      onClick={handleBuildFromScratch}
                      className="mt-4 w-full bg-secondary text-foreground font-semibold py-2.5 rounded-lg hover:bg-muted transition-colors"
                    >
                      Start Blank
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground">
                      Start with an empty form and fill in all the details for your tournament from scratch.
                    </p>

                    <button
                      onClick={() => setSelectedPath("scratch")}
                      className="mt-4 w-full bg-secondary text-foreground font-semibold py-2.5 rounded-lg hover:bg-muted transition-colors"
                    >
                      Start Blank
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate("/events")}
              className="text-primary hover:underline text-sm font-medium"
            >
              ← Back to Events
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
